import PropTypes from 'prop-types';
import path from 'path';
import React from 'react';
import { ListGroup, ListGroupItem, Popover, PopoverHeader, PopoverBody } from 'reactstrap';
import { createHashHistory } from 'history';
import entityComposition from '../../../common/classes/entityComposition';
import mapSettingsSelector from '../../selectors/mapSettingsSelector';
import { checkPermission, makeResource } from '../../../../../behaviors/src/lib/permissions';
import Icon from '../../../common/containers/icon';
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px'
  },
  popoverBody: {
    padding: '10px'
  },
  row: {
    marginBottom: '5px'
  },
  span: {
    marginLeft: '5px',
    fontStyle: 'normal'
  },
  icon: {
    opacity: '0.25',
    cursor: 'pointer'
  },
  listGroup: {
    maxHeight: '250px',
    overflowY: 'auto',
    border: '1px solid #ddd',
    padding: '0'
  },
  listGroupItem: {
    padding: '10px',
    borderBottom: '1px solid #ddd',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: {
    fontWeight: 'bold',
    marginBottom: '5px'
  }
};
const hashHistory = createHashHistory();
const ClusterCallout = ({
  login: {
    user,
    user: {
      permissions
    }
  },
  map: {
    clusterItems
  },
  notificationActions: {
    info
  },
  setSelectedMarker,
  target,
  cabinetIdentifier: _cabinetIdentifier = null,
  mapSettings: _mapSettings = {},
  selectedMarker: _selectedMarker = null
}) => {
  if (!_selectedMarker || !_selectedMarker.cluster) return null;
  const pinStyle = _mapSettings.pinStyles[_cabinetIdentifier] || {};
  const pinStyleTitleIndex = pinStyle.titleIndex || null;

  /**
   * Navigate to formEditor when a cluster asset is clicked
   */
  const handleFormClick = formID => {
    let selectedForm;
    if (_selectedMarker.cluster && clusterItems && Array.isArray(clusterItems.features) && clusterItems.features.length > 0) {
      selectedForm = clusterItems.features.find(f => f.properties.id === formID);
    }
    if (!selectedForm) return;
    const {
      formTypeIdentifier
    } = selectedForm.properties;
    const canViewDetail = checkPermission(makeResource('form', formTypeIdentifier), 'view_detail', permissions, user);
    if (canViewDetail !== true) {
      info({
        headerMessage: 'Permissions',
        message: `You do not have permission to view the details of ${selectedForm.properties.formTypeIdentifier}.`,
        id: 'transition_while_resizing'
      });
      return;
    }
    const url = path.join('/cabinets', _cabinetIdentifier, formTypeIdentifier, formID);
    hashHistory.push(url);
    setSelectedMarker(null);
  };

  /**
   * Safeguard to ensure clusterItems is properly structured
   */
  const normalizedClusterItems = clusterItems && Array.isArray(clusterItems.features) ? clusterItems : {
    type: 'FeatureCollection',
    features: []
  };
  const ensurePropIsArray = value => {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      if (value.trim().startsWith('[') && value.trim().endsWith(']')) {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return parsed;
          }
        } catch (_unused) {
          throw new Error('Failed to parse cluster properties');
        }
      }
      if (value.indexOf(',') !== -1) {
        return value.split(',').map(s => s.trim());
      }
      return [value];
    }
    return [];
  };

  /**
   * Render the list of items within the cluster
   */
  const renderClusterItems = () => {
    if (normalizedClusterItems.features.length === 0) {
      return /*#__PURE__*/React.createElement("div", null, "No assets available in this cluster.");
    }
    return /*#__PURE__*/React.createElement(ListGroup, {
      style: styles.listGroup
    }, normalizedClusterItems.features.map(feature => {
      const markerLabel = feature.properties.markerLabel ? feature.properties.markerLabel : feature.properties.title || `Asset ${feature.properties.id}`;
      return /*#__PURE__*/React.createElement(ListGroupItem, {
        key: feature.properties.id,
        style: styles.listGroupItem,
        onClick: () => handleFormClick(feature.properties.id)
      }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("strong", {
        style: styles.title
      }, markerLabel), (_mapSettings$calloutL => {
        const formType = feature.properties.formTypeIdentifier;
        const layout = ((_mapSettings$calloutL = _mapSettings.calloutLayouts[formType]) === null || _mapSettings$calloutL === void 0 ? void 0 : _mapSettings$calloutL.rows) || [];
        if (!layout.length) {
          return /*#__PURE__*/React.createElement("div", null, "No data available");
        }
        return layout.map(row => {
          if (row.index === pinStyleTitleIndex) {
            return null;
          }
          const key = `callout-row-${feature.properties.id}-${row.index}`;
          const rawValue = feature.properties[row.index];
          const arrValue = ensurePropIsArray(rawValue);
          const displayValue = Array.isArray(arrValue) ? arrValue.join(row.separator || ', ') : arrValue;
          return /*#__PURE__*/React.createElement("div", {
            key: key,
            style: styles.row
          }, /*#__PURE__*/React.createElement("strong", null, `${row.title}:`), /*#__PURE__*/React.createElement("span", {
            style: styles.span
          }, displayValue));
        });
      })()), /*#__PURE__*/React.createElement(Icon, {
        glyph: "angle-right",
        style: styles.icon
      }));
    }));
  };
  return /*#__PURE__*/React.createElement(Popover, {
    "data-testid": "cluster-popover",
    target: target,
    placement: "bottom",
    isOpen: Boolean(_selectedMarker),
    style: {
      width: `${_mapSettings.calloutSize.width}px`
    }
  }, /*#__PURE__*/React.createElement(PopoverHeader, {
    style: styles.container
  }, /*#__PURE__*/React.createElement("strong", null, "Cluster (", normalizedClusterItems.features.length, " item", normalizedClusterItems.features.length !== 1 ? 's' : '', ")")), /*#__PURE__*/React.createElement(PopoverBody, {
    style: styles.popoverBody
  }, renderClusterItems()));
};
ClusterCallout.propTypes = {
  cabinetIdentifier: PropTypes.string,
  selectedMarker: PropTypes.shape({
    cluster: PropTypes.bool
  }),
  map: PropTypes.shape({
    clusterItems: PropTypes.shape({
      features: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }).isRequired,
  mapSettings: PropTypes.shape({
    pinStyles: PropTypes.shape({}),
    calloutSize: PropTypes.shape({
      width: PropTypes.number
    }).isRequired,
    calloutLayouts: PropTypes.objectOf(PropTypes.shape({
      rows: PropTypes.arrayOf(PropTypes.shape({}))
    }))
  }),
  notificationActions: PropTypes.shape({
    info: PropTypes.func
  }).isRequired,
  login: PropTypes.shape({
    user: PropTypes.shape({
      permissions: PropTypes.shape({})
    })
  }).isRequired,
  setSelectedMarker: PropTypes.func.isRequired,
  target: PropTypes.shape({}).isRequired
};
export { ClusterCallout as DisconnectedClusterCallout };
export default entityComposition(ClusterCallout, ['map', 'login'], {
  mapSettings: mapSettingsSelector
}, ['notification']);